import { Agent } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo, useState } from 'react';

import { DEFAULT_TEMPLATE, TEMPLATES } from '@/components/ui';
import { NewAgent } from '@/features/agents';
import { convertToFieldConfig } from '@/utils';

import { useCreateAgent } from './api';
import { useAddExport } from './useAddExport';
import { useAddModel } from './useAddModel';
import { useAddTrigger } from './useAddTrigger';
import { useAddValidation } from './useAddValidation';
import { useAgent } from './useAgent.ts';

const prepareFieldConfigAndSamples = async (
  selectedTemplate: keyof typeof TEMPLATES | undefined,
  state: Partial<NewAgent> | undefined
) => {
  let fieldConfig = state?.fieldConfig ?? {};
  let templateSamples: File[] = [];
  const userProvidedSamples: File[] = state?.sampleDocuments ?? [];

  if (selectedTemplate && selectedTemplate !== DEFAULT_TEMPLATE) {
    fieldConfig = convertToFieldConfig(TEMPLATES[selectedTemplate]?.fields ?? []);

    templateSamples = await Promise.all(
      TEMPLATES[selectedTemplate]?.sampleDocuments?.map(async (sampleDocumentUri: string) => {
        const blob = await (await fetch(sampleDocumentUri)).blob();
        const name = decodeURI(sampleDocumentUri.split('/').at(-1) ?? 'sample');
        return new File([blob], name, { type: blob.type });
      })
    );
  }

  return { fieldConfig, templateSamples, userProvidedSamples };
};

type Status = {
  isSuccess: boolean;
  isPending: boolean;
  errors: Error[];
};

export type ScaffoldAgentStatus = {
  model: Status;
  trigger: Status;
  export: Status;
  validation: Status;
  agent: Status;
  errors: Error[];
  isDone: boolean;
};

export const useScaffoldAgent = () => {
  const [errors, setErrors] = useState<Error[]>([]);
  const [agentId, setAgentId] = useState<string>();
  const [hasSelectedTrigger, setHasSelectedTrigger] = useState(false);
  const [hasSelectedExport, setHasSelectedExport] = useState(false);
  const { createRun } = useAgent();

  const {
    mutate: createAgent,
    isSuccess: isSuccessCreateAgent,
    error: errorCreateAgent,
    isPending: isPendingCreateAgent,
  } = useCreateAgent();

  const {
    addModel,
    isPending: isPendingAddModel,
    isSuccess: isSuccessAddModel,
    errors: errorsAddModel,
  } = useAddModel();
  const {
    addValidation,
    isPending: isPendingAddValidation,
    isSuccess: isSuccessAddValidation,
    errors: errorsAddValidation,
  } = useAddValidation();
  const {
    addTrigger,
    isSuccess: isSuccessAddTrigger,
    isPending: isPendingAddTrigger,
    errors: errorsAddTrigger,
  } = useAddTrigger();
  const {
    addExport,
    isSuccess: isSuccessAddExport,
    isPending: isPendingAddExport,
    errors: errorsAddExport,
  } = useAddExport();

  const create = useCallback(
    (state?: Partial<NewAgent>): Partial<NewAgent> => {
      const selectedTemplate = state?.template;
      const selectedTrigger = state?.trigger;
      const selectedExport = state?.export;

      setHasSelectedTrigger(!!selectedTrigger);
      setHasSelectedExport(!!selectedExport);

      createAgent(
        { name: state?.agentName || selectedTemplate },
        {
          onSuccess: async (agent: Agent) => {
            const agentId = agent.agentId;

            const { fieldConfig, templateSamples, userProvidedSamples } = await prepareFieldConfigAndSamples(
              selectedTemplate,
              state
            );

            setAgentId(agent.agentId);

            try {
              const model = await addModel({ agentId, fieldConfig });

              if (model) {
                await addValidation({ agentId, modelId: model.modelId });
                if (selectedTrigger) await addTrigger({ agentId, triggerOption: selectedTrigger });
                if (selectedExport) await addExport({ agentId, exportOption: selectedExport });
              }

              for (const sampleDocument of userProvidedSamples) {
                await createRun({
                  file: sampleDocument,
                  agentId: agentId,
                  variables: { requireReview: { value: true } },
                });
              }

              for (const sampleDocument of templateSamples) {
                await createRun({
                  file: sampleDocument,
                  agentId: agentId,
                  variables: { isSampleDocument: { value: true }, requireReview: { value: true } },
                });
              }
            } catch (error) {
              if (error instanceof Error) {
                setErrors((errors) => [...errors, error]);
              }
            }
          },
        }
      );

      return state || {};
    },
    [addExport, addModel, addTrigger, addValidation, createAgent, createRun]
  );

  const isDone = useMemo(() => {
    const isDoneAddModel = isSuccessAddModel;
    const isDoneAddValidation = isSuccessAddValidation;
    const isDoneAddTrigger = !hasSelectedTrigger || isSuccessAddTrigger;
    const isDoneAddExport = !hasSelectedExport || isSuccessAddExport;
    return isDoneAddModel && isDoneAddValidation && isDoneAddTrigger && isDoneAddExport;
  }, [
    hasSelectedExport,
    hasSelectedTrigger,
    isSuccessAddExport,
    isSuccessAddModel,
    isSuccessAddTrigger,
    isSuccessAddValidation,
  ]);

  return {
    agentId,
    status: {
      isDone,
      model: { isSuccess: isSuccessAddModel, isPending: isPendingAddModel, errors: errorsAddModel ?? [] },
      export: { isPending: isPendingAddExport, isSuccess: isSuccessAddExport, errors: errorsAddExport ?? [] },
      trigger: { isPending: isPendingAddTrigger, isSuccess: isSuccessAddTrigger, errors: errorsAddTrigger ?? [] },
      validation: {
        isSuccess: isSuccessAddValidation,
        isPending: isPendingAddValidation,
        errors: errorsAddValidation ?? [],
      },
      agent: {
        isSuccess: isSuccessCreateAgent,
        isPending: isPendingCreateAgent,
        errors: errorCreateAgent ? [errorCreateAgent] : [],
      },
      errors,
    },
    hasSelectedTrigger,
    hasSelectedExport,
    create,
  };
};
